import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  ArrowDownIcon,
  PlusIcon,
  ReportIcon,
  AppleIcon,
  GoogleIcon,
} from "./icons";
import { AppStore, AppInsightsProps } from "./types";
import { AppInsightsTabs } from "../AppInsightsTabs";

const STORE_ICON: Record<AppStore, React.FC<React.SVGProps<SVGSVGElement>>> = {
  apple: AppleIcon,
  google: GoogleIcon,
};

const STORE_LABEL: Record<AppStore, string> = {
  apple: "App Store",
  google: "Google Play",
};

const AppLogo: React.FC<{ icon?: React.ReactNode; name: string }> = ({
  icon,
  name,
}) => {
  if (typeof icon === "string" && icon) {
    return <img className={styles.appIcon} src={icon} alt="" />;
  }
  if (icon) {
    return <span className={styles.appIcon}>{icon}</span>;
  }
  return (
    <span className={cs(styles.appIcon, styles.appIconFallback)}>
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
};

const Flag: React.FC<{ flag?: React.ReactNode }> = ({ flag }) => {
  if (typeof flag === "string" && flag) {
    return <img className={styles.flag} src={flag} alt="" />;
  }
  if (flag) {
    return <span className={styles.flag}>{flag}</span>;
  }
  return <span className={cs(styles.flag, styles.flagFallback)} aria-hidden />;
};

/**
 * App-insights card (Figma 599:7298, action row 585:24903). Pure
 * presentational: app header (icon + name dropdown + developer/id + "View in
 * store" link), an action row (country select, outlined "ASO Report" and
 * "New Order" buttons, equal widths) and an info row (Categories / Price /
 * Content rating). White card surface flips in dark mode.
 */
export const AppInsights: React.FC<AppInsightsProps> = ({
  icon,
  name,
  developer,
  appId,
  store = "apple",
  onAppClick,
  onViewInStore,
  country,
  onCountryClick,
  onAsoReport,
  asoReportActive = false,
  onNewOrder,
  categories,
  price,
  contentRating,
  tabs,
  activeTab,
  onTabChange,
  iconSlot,
  dropdownSlot,
  metaSlot,
  viewInStoreSlot,
  countrySlot,
  asoReportSlot,
  newOrderSlot,
  tabsSlot,
  className,
}) => {
  const StoreIcon = STORE_ICON[store];

  return (
    <div className={cs(styles.card, className)}>
      {/* ---- header ---- */}
      <div className={styles.header}>
        {iconSlot ?? <AppLogo icon={icon} name={name} />}

        <div className={styles.info}>
          {dropdownSlot ?? (
            <button
              type="button"
              className={styles.appDropdown}
              onClick={onAppClick}
            >
              <span className={styles.appNameBox}>
                <span className={styles.appName}>{name}</span>
              </span>
              <ArrowDownIcon className={styles.icon20} />
            </button>
          )}

          {metaSlot ?? (
            <div className={styles.meta}>
              {developer && (
                <span className={styles.metaItem}>{developer}</span>
              )}
              {developer && appId != null && (
                <span className={styles.metaDot}>•</span>
              )}
              {appId != null && (
                <span className={styles.metaItem}>ID {appId}</span>
              )}
            </div>
          )}

          {viewInStoreSlot ?? (
            <button
              type="button"
              className={styles.viewInStore}
              onClick={onViewInStore}
            >
              <span className={styles.storeIconBox}>
                <StoreIcon className={styles.storeIcon} />
              </span>
              <span className={styles.viewInStoreText}>
                View in {STORE_LABEL[store]}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ---- action row ---- */}
      <div className={styles.actions}>
        <div className={styles.slotFill}>
          {countrySlot ?? (
            <button
              type="button"
              className={styles.select}
              onClick={onCountryClick}
            >
              <span className={styles.selectValue}>
                <Flag flag={country?.flag} />
                <span className={styles.selectLabel}>
                  {country?.label ?? "Select country"}
                </span>
              </span>
              <ArrowDownIcon className={styles.icon20} />
            </button>
          )}
        </div>

        {(asoReportSlot || onAsoReport) && (
          <div className={styles.slotFill}>
            {asoReportSlot ?? (
              <button
                type="button"
                className={cs(styles.asoReport, {
                  [styles.asoReportActive]: asoReportActive,
                })}
                onClick={onAsoReport}
                aria-pressed={asoReportActive}
              >
                <ReportIcon className={styles.icon20} />
                ASO Report
              </button>
            )}
          </div>
        )}

        <div className={styles.slotFill}>
          {newOrderSlot ?? (
            <button
              type="button"
              className={styles.newOrder}
              onClick={onNewOrder}
            >
              <PlusIcon className={styles.icon20} />
              New Order
            </button>
          )}
        </div>
      </div>

      {/* ---- info row ---- */}
      <div className={styles.infoRow}>
        <div className={styles.infoCell}>
          <span className={styles.infoLabel}>Categories</span>
          <span className={styles.infoValue}>{categories ?? "--"}</span>
        </div>
        <div className={styles.infoCell}>
          <span className={styles.infoLabel}>Price</span>
          <span className={styles.infoValue}>{price ?? "--"}</span>
        </div>
        <div className={cs(styles.infoCell, styles.infoCellEnd)}>
          <span className={styles.infoLabel}>Content rating</span>
          <span className={styles.infoValue}>{contentRating ?? "--"}</span>
        </div>
      </div>

      {/* ---- section tabs (App Profile / ASO Report) ---- */}
      {tabsSlot ??
        (tabs && tabs.length > 0 ? (
          <AppInsightsTabs
            className={styles.tabs}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
        ) : null)}
    </div>
  );
};

export default AppInsights;
